import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, MessageCircle, X } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";
import { CONTACT, SERVICES } from "@/data/site";

const NAV_LINKS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/site-inspection", label: "Site Inspection" },
    { to: "/boq-bill-review", label: "BOQ & Bill Review" },
    { to: "/how-it-works", label: "How It Works" },
    { to: "/contact", label: "Contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const linkClass = ({ isActive }) =>
        `font-mono text-[11px] uppercase tracking-[0.16em] transition-colors duration-200 ${
            isActive ? "text-amber" : "text-slate-600 hover:text-navy"
        }`;

    return (
        <header
            className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-xl"
            data-testid="site-header"
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-12">
                <Logo />

                <nav className="hidden items-center gap-7 xl:flex" aria-label="Primary">
                    <NavLink to="/" className={linkClass} data-testid="nav-home" end>
                        Home
                    </NavLink>
                    <NavLink to="/about" className={linkClass} data-testid="nav-about">
                        About
                    </NavLink>
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-600 outline-none transition-colors duration-200 hover:text-navy data-[state=open]:text-amber"
                            data-testid="nav-services-dropdown"
                        >
                            Services <ChevronDown size={13} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-80 rounded-none border-slate-200 p-2">
                            <DropdownMenuItem asChild className="rounded-none">
                                <Link to="/services" className="cursor-pointer font-medium text-navy" data-testid="nav-services-all">
                                    All Services
                                </Link>
                            </DropdownMenuItem>
                            {SERVICES.map((s) => (
                                <DropdownMenuItem key={s.title} asChild className="rounded-none">
                                    <Link
                                        to={s.link}
                                        className="cursor-pointer text-slate-600"
                                        data-testid={`nav-service-${s.short.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    >
                                        {s.title}
                                    </Link>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                    {NAV_LINKS.slice(2).map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            className={linkClass}
                            data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden items-center gap-3 xl:flex">
                    <Link
                        to="/contact"
                        className="bg-navy px-6 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-amber"
                        data-testid="header-book-consultation-btn"
                    >
                        Book a Consultation
                    </Link>
                </div>

                <button
                    className="flex h-11 w-11 items-center justify-center border border-slate-200 text-navy xl:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Close menu" : "Open menu"}
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="overflow-hidden border-t border-slate-200 bg-white xl:hidden"
                        data-testid="mobile-menu"
                    >
                        <nav className="flex flex-col px-6 py-6" aria-label="Mobile">
                            {NAV_LINKS.slice(0, 2).map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.to === "/"}
                                    className="border-b border-slate-100 py-3.5 font-display text-lg font-bold text-navy"
                                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <NavLink
                                to="/services"
                                className="border-b border-slate-100 py-3.5 font-display text-lg font-bold text-navy"
                                data-testid="mobile-nav-services"
                            >
                                Services
                            </NavLink>
                            <div className="grid grid-cols-1 gap-1 border-b border-slate-100 py-3 pl-4">
                                {SERVICES.map((s) => (
                                    <Link
                                        key={s.title}
                                        to={s.link}
                                        className="py-1.5 text-sm text-slate-600"
                                        data-testid={`mobile-service-${s.short.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    >
                                        {s.title}
                                    </Link>
                                ))}
                            </div>
                            {NAV_LINKS.slice(2).map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    className="border-b border-slate-100 py-3.5 font-display text-lg font-bold text-navy"
                                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <div className="mt-6 flex flex-col gap-3">
                                <Link
                                    to="/contact"
                                    className="bg-navy px-6 py-4 text-center font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white"
                                    data-testid="mobile-book-consultation-btn"
                                >
                                    Book a Consultation
                                </Link>
                                <a
                                    href={CONTACT.whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 bg-[#25D366] px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white"
                                    data-testid="mobile-whatsapp-btn"
                                >
                                    <MessageCircle size={16} /> Chat on WhatsApp
                                </a>
                            </div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
